/** @jsxImportSource preact */
/**
 * ShopperLoginPanel — inline login component rendered when chat_ms emits
 * an `auth_request` envelope message offering "log in for 1-click /
 * saved cards".
 *
 * Flow (email-OTP):
 *   1. User types email → click "Enviar código" →
 *      `auth.shopper_login.v1` ToolDispatch goes through the dispatcher.
 *   2. Server emits a code; user receives via email.
 *   3. User types 6-digit code → click "Verificar" →
 *      `auth.shopper_verify.v1` ToolDispatch dispatched.
 *   4. On success, server returns the `shopper_token` via the chat
 *      envelope; the widget shell stores it via `StorageService.setShopperToken`.
 *
 * SSO buttons (Google / Facebook): they open an OAuth window pointing
 * at `auth_ms`. When auth_ms completes the flow, it postMessages the
 * shopper_token back; the widget shell listens and persists it. This
 * component is a stub for the OAuth window opener — the wiring lives
 * in the widget shell since postMessage transport spans iframe boundary.
 *
 * Accessibility:
 *   - Native `<form>` with `<label>`s, role implicit.
 *   - Errors are announced via `aria-live="polite"`.
 *   - Tab order follows visual flow; no tabindex hacks.
 */

import { useState } from 'preact/hooks';
import type { DispatchHandler } from '../types';
import { t } from '../../../i18n';

export interface ShopperLoginPanelProps {
  /** Receives the validated ToolDispatch (auth.shopper_login.v1 / verify). */
  onDispatch: DispatchHandler;
  /** Optional callback when the user clicks an SSO button. */
  onSsoClick?: (provider: 'google' | 'facebook') => void;
  /**
   * Optional initial email (e.g. when host page knows the customer
   * already from a previous interaction).
   */
  initialEmail?: string;
  /**
   * External step override. Useful when the parent re-renders after the
   * server confirms the code was sent and switches to "verify" step.
   */
  step?: 'email' | 'code';
  /** Set true while a dispatch is in-flight to disable the buttons. */
  loading?: boolean;
  /** Server-surfaced error to render inline (e.g. "código inválido"). */
  errorMessage?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_RE = /^[0-9]{4,8}$/;

export function ShopperLoginPanel({
  onDispatch,
  onSsoClick,
  initialEmail,
  step,
  loading,
  errorMessage,
}: ShopperLoginPanelProps) {
  const [email, setEmail] = useState(initialEmail || '');
  const [code, setCode] = useState('');
  const [localStep, setLocalStep] = useState<'email' | 'code'>(step || 'email');
  const [localError, setLocalError] = useState<string | null>(null);

  const effectiveStep = step || localStep;
  const effectiveError = errorMessage || localError;

  const submitEmail = (e: Event) => {
    e.preventDefault();
    setLocalError(null);
    if (!EMAIL_RE.test(email)) {
      setLocalError(t('login.invalidEmail'));
      return;
    }
    onDispatch({
      tool: 'auth.shopper_login.v1',
      args: { email },
      args_schema_ref: 'auth.shopper_login.v1',
      client_dispatch_id: `login_${Date.now()}`,
    });
    setLocalStep('code');
  };

  const submitCode = (e: Event) => {
    e.preventDefault();
    setLocalError(null);
    if (!CODE_RE.test(code)) {
      setLocalError(t('login.invalidCode'));
      return;
    }
    onDispatch({
      tool: 'auth.shopper_verify.v1',
      args: { email, code },
      args_schema_ref: 'auth.shopper_verify.v1',
      client_dispatch_id: `verify_${Date.now()}`,
    });
  };

  return (
    <div class="lc-v2-shopper-login" role="region" aria-label={t('login.region')}>
      {effectiveStep === 'email' ? (
        <form class="lc-v2-shopper-login__form" onSubmit={submitEmail} noValidate>
          <label class="lc-v2-shopper-login__label" for="lc-v2-shopper-email">
            {t('login.email')}
          </label>
          <input
            id="lc-v2-shopper-email"
            class="lc-v2-shopper-login__input"
            type="email"
            autocomplete="email"
            inputMode="email"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            disabled={loading}
            aria-invalid={!!effectiveError}
            aria-describedby={effectiveError ? 'lc-v2-shopper-error' : undefined}
            required
          />
          <button
            type="submit"
            class="lc-v2-shopper-login__btn lc-v2-shopper-login__btn--primary"
            disabled={loading}
            aria-label={t('login.sendCodeAria')}
          >
            {loading ? t('login.sending') : t('login.sendCode')}
          </button>
        </form>
      ) : (
        <form class="lc-v2-shopper-login__form" onSubmit={submitCode} noValidate>
          <label class="lc-v2-shopper-login__label" for="lc-v2-shopper-code">
            {t('login.codeLabel')}
          </label>
          <input
            id="lc-v2-shopper-code"
            class="lc-v2-shopper-login__input"
            type="text"
            inputMode="numeric"
            autocomplete="one-time-code"
            pattern="[0-9]*"
            value={code}
            onInput={(e) => setCode((e.target as HTMLInputElement).value)}
            disabled={loading}
            aria-invalid={!!effectiveError}
            aria-describedby={effectiveError ? 'lc-v2-shopper-error' : undefined}
            required
          />
          <button
            type="submit"
            class="lc-v2-shopper-login__btn lc-v2-shopper-login__btn--primary"
            disabled={loading}
            aria-label={t('login.verifyAria')}
          >
            {loading ? t('login.verifying') : t('login.verify')}
          </button>
          <button
            type="button"
            class="lc-v2-shopper-login__btn lc-v2-shopper-login__btn--secondary"
            onClick={() => setLocalStep('email')}
            disabled={loading}
            aria-label={t('login.changeEmail')}
          >
            {t('login.changeEmail')}
          </button>
        </form>
      )}

      {onSsoClick ? (
        <div class="lc-v2-shopper-login__sso" role="group" aria-label={t('login.ssoGroup')}>
          <button
            type="button"
            class="lc-v2-shopper-login__sso-btn"
            onClick={() => onSsoClick('google')}
            disabled={loading}
            aria-label={t('login.continueWith', { provider: 'Google' })}
          >
            {t('login.continueWith', { provider: 'Google' })}
          </button>
          <button
            type="button"
            class="lc-v2-shopper-login__sso-btn"
            onClick={() => onSsoClick('facebook')}
            disabled={loading}
            aria-label={t('login.continueWith', { provider: 'Facebook' })}
          >
            {t('login.continueWith', { provider: 'Facebook' })}
          </button>
        </div>
      ) : null}

      {effectiveError ? (
        <div
          id="lc-v2-shopper-error"
          class="lc-v2-shopper-login__error"
          role="alert"
          aria-live="polite"
        >
          {effectiveError}
        </div>
      ) : null}
    </div>
  );
}
